import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import styles from "./LegalUpdatePopup.module.css";
import { useTranslation } from "react-i18next";

/**
 * LegalUpdatePopup component
 * Refactored to use getDoc for stability, performing a single check on mount
 * to avoid real-time listener overhead and potential SDK assertion errors.
 */
const LegalUpdatePopup = () => {
  const { t } = useTranslation();
  const [showPopup, setShowPopup] = useState(false);
  const [updates, setUpdates] = useState([]); // List of types that changed: 'privacy', 'terms'
  const versionsRef = useRef({ privacy: null, terms: null });

  useEffect(() => {
    const checkLegalVersions = async () => {
      try {
        const privacyRef = doc(db, "legal", "privacy");
        const termsRef = doc(db, "legal", "terms");

        // Fetch current versions of legal documents
        const [privacySnap, termsSnap] = await Promise.all([
          getDoc(privacyRef),
          getDoc(termsRef)
        ]);

        const newUpdates = [];

        if (privacySnap.exists()) {
          const v = privacySnap.data().version || "1.0.0";
          versionsRef.current.privacy = v;
          if (isVersionDifferent("privacy", v)) {
            newUpdates.push("privacy");
          }
        }

        if (termsSnap.exists()) {
          const v = termsSnap.data().version || "1.0.0";
          versionsRef.current.terms = v;
          if (isVersionDifferent("terms", v)) {
            newUpdates.push("terms");
          }
        }

        if (newUpdates.length > 0) {
          setUpdates(newUpdates);
          setShowPopup(true);
        }
      } catch (err) {
        console.error("Error checking legal versions:", err);
      }
    };

    checkLegalVersions();
  }, []);

  /**
   * Helper to check if the version in Firestore differs from the one in local storage.
   * If no version exists in local storage, we record the current one and don't show a popup.
   */
  const isVersionDifferent = (type, currentVersion) => {
    const key = `legal_version_${type}`;
    const storedVersion = localStorage.getItem(key);

    if (!storedVersion) {
      // First time user or storage cleared: record the current version and don't notify
      localStorage.setItem(key, currentVersion);
      return false;
    }

    return storedVersion !== currentVersion;
  };

  const handleAcknowledge = () => {
    // Mark current versions as seen in localStorage
    updates.forEach((type) => {
      const latestVersion = versionsRef.current[type];
      if (latestVersion) {
        localStorage.setItem(`legal_version_${type}`, latestVersion);
      }
    });
    
    setUpdates([]);
    setShowPopup(false);
  };

  if (!showPopup || updates.length === 0) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.popup}>
        <div className={styles.icon}>⚖️</div>
        <h2 className={styles.title}>{t('legal.updateTitle', 'Legal Update')}</h2>
        <p className={styles.message}>
          {t('legal.updateMessage', 'We have updated our legal documents. Please review the changes to continue using PeaksOnFoot.')}
        </p>
        
        <div className={styles.links}>
          {updates.includes('terms') && (
            <Link to="/terms" onClick={() => setShowPopup(false)} className={styles.legalLink}>
              {t('common.termsOfService', 'Terms of Service')}
            </Link>
          )}
          {updates.includes('privacy') && (
            <Link to="/privacy" onClick={() => setShowPopup(false)} className={styles.legalLink}>
              {t('common.privacyPolicy', 'Privacy Policy')}
            </Link>
          )}
        </div>

        <button className={styles.closeButton} onClick={handleAcknowledge}>
          {t('action.understand', 'I Understand')}
        </button>
      </div>
    </div>
  );
};

export default LegalUpdatePopup;